import React, { useEffect, useState } from 'react';
import { statsApi, channelsApi, subscriptionsApi, paymentsApi } from '../lib/api';
import { 
  TrendingUp, 
  Users, 
  CreditCard, 
  Tv,
  ArrowUpRight,
  Clock
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const StatCard = ({ icon: Icon, label, value, subValue, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-[#2AABEE]/10 text-[#2AABEE]',
    green: 'bg-emerald-500/10 text-emerald-400',
    purple: 'bg-purple-500/10 text-purple-400',
    yellow: 'bg-yellow-500/10 text-yellow-400',
  };

  return (
    <div className="stat-card animate-fade-in" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        {subValue && (
          <span className="text-xs text-emerald-400 flex items-center gap-1">
            <ArrowUpRight className="w-3 h-3" />
            {subValue}
          </span>
        )}
      </div>
      <p className="stat-value">{value}</p>
      <p className="stat-label mt-1">{label}</p>
    </div>
  );
};

const RecentPayment = ({ payment, channel }) => {
  const statusColors = {
    confirmed: 'badge-active',
    pending: 'badge-pending',
    cancelled: 'badge-cancelled',
  };

  const statusLabels = {
    confirmed: 'Подтвержден',
    pending: 'Ожидает',
    cancelled: 'Отменен',
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
          <CreditCard className="w-4 h-4 text-zinc-400" />
        </div>
        <div>
          <p className="text-sm text-white">@{payment.user_telegram_id}</p>
          <p className="text-xs text-zinc-500">{channel?.name || 'Канал'}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm font-medium text-white">{payment.amount} {payment.currency}</p>
        <span className={`badge ${statusColors[payment.status]}`}>
          {statusLabels[payment.status]}
        </span>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [channels, setChannels] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, channelsRes, paymentsRes] = await Promise.all([
          statsApi.get(),
          channelsApi.getAll(),
          paymentsApi.getAll({ limit: 5 })
        ]);
        setStats(statsRes.data);
        setChannels(channelsRes.data);
        setRecentPayments(paymentsRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Дашборд</h1>
        <p className="text-zinc-500">Обзор статистики и последних событий</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
        <StatCard
          icon={TrendingUp}
          label="Общий доход"
          value={formatCurrency(stats?.revenue?.total)}
          subValue={`+${formatCurrency(stats?.revenue?.recent_7_days)} за неделю`}
          color="green"
        />
        <StatCard
          icon={Users}
          label="Активные подписки"
          value={stats?.subscriptions?.active || 0}
          subValue={`из ${stats?.subscriptions?.total || 0} всего`}
          color="blue"
        />
        <StatCard
          icon={CreditCard}
          label="Ожидающие платежи"
          value={stats?.payments?.pending || 0}
          color="yellow"
        />
        <StatCard
          icon={Tv}
          label="Активные каналы"
          value={stats?.channels?.active || 0}
          subValue={`из ${stats?.channels?.total || 0} всего`}
          color="purple"
        />
      </div>

      {/* Charts and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 card">
          <div className="card-header">
            <h3 className="card-title">Доходы за 30 дней</h3>
          </div>
          <div className="card-content">
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats?.daily_stats || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#71717a" 
                    fontSize={12}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis stroke="#71717a" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0a0a0a', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="amount" 
                    stroke="#2AABEE" 
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: '#2AABEE' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Recent Payments */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Последние платежи</h3>
            <Clock className="w-4 h-4 text-zinc-500" />
          </div>
          <div className="card-content">
            {recentPayments.length > 0 ? (
              <div data-testid="recent-payments-list">
                {recentPayments.map((payment) => (
                  <RecentPayment 
                    key={payment.id} 
                    payment={payment}
                    channel={channels.find(c => c.id === payment.channel_id)}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <CreditCard className="empty-state-icon" />
                <p>Платежей пока нет</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
